import React, { useState } from "react";
import {
  makeStyles,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  tokens,
} from "@fluentui/react-components";
import { Calendar16Regular, ChevronDown12Regular } from "@fluentui/react-icons";
import { addDays, type ISODate } from "@/engine/date";

const useStyles = makeStyles({
  dateTrigger: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: "none",
    border: "none",
    padding: "2px 4px",
    borderRadius: "4px",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    color: "inherit",
    transition: "background-color 0.15s ease",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
    "@media print": {
      padding: 0,
      margin: 0,
      backgroundColor: "transparent !important",
      cursor: "default !important",
    },
  },
  readOnly: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 4px",
  },
  relativeBadge: {
    marginLeft: "4px",
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
    "@media print": {
      display: "none !important",
    },
  },
  caret: {
    display: "inline-flex",
    color: tokens.colorNeutralForeground3,
    "@media print": {
      display: "none !important",
    },
  },
  popoverBody: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "8px",
    minWidth: "160px",
  },
  chipsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
  },
  chip: {
    padding: "2px 8px",
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  dateInput: {
    fontFamily: "inherit",
    fontSize: "12px",
    padding: "4px 8px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: "4px",
    outline: "none",
    ":focus": {
      borderTopColor: tokens.colorBrandStroke1,
      borderRightColor: tokens.colorBrandStroke1,
      borderBottomColor: tokens.colorBrandStroke1,
      borderLeftColor: tokens.colorBrandStroke1,
      boxShadow: `0 0 0 1px ${tokens.colorBrandStroke1}`,
    },
  },
});

export interface DatePresetItem {
  label: string;
  iso: string;
}

export interface InlineDatePopoverProps {
  value: string;
  onChange: (newDate: string) => void;
  statusDate?: string;
  presets?: DatePresetItem[];
  showRelativeBadge?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  style?: React.CSSProperties;
}

function computeRelativeDays(targetDate: string, baseDate?: string): string | null {
  if (!baseDate || !targetDate) return null;
  try {
    const t = new Date(targetDate).getTime();
    const b = new Date(baseDate).getTime();
    if (isNaN(t) || isNaN(b)) return null;
    const diffDays = Math.round((t - b) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "D-Day";
    if (diffDays > 0) return `D+${diffDays}`;
    return `D${diffDays}`;
  } catch {
    return null;
  }
}

export function InlineDatePopover({
  value,
  onChange,
  statusDate,
  presets,
  showRelativeBadge = false,
  readOnly = false,
  placeholder = "날짜 선택",
  ariaLabel,
  className,
  style,
}: InlineDatePopoverProps) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);

  const defaultPresets: DatePresetItem[] = React.useMemo(() => {
    if (presets && presets.length > 0) return presets;
    const base = (statusDate || new Date().toISOString().slice(0, 10)) as ISODate;
    return [
      { label: "오늘", iso: base },
      { label: "어제", iso: addDays(base, -1) },
      { label: "내일", iso: addDays(base, 1) },
    ];
  }, [presets, statusDate]);

  const relBadgeText = showRelativeBadge && statusDate ? computeRelativeDays(value, statusDate) : null;

  if (readOnly) {
    return (
      <span className={`${styles.readOnly} ${className || ""}`} style={style} aria-label={ariaLabel}>
        <span>{value || placeholder}</span>
        {relBadgeText ? <span className={styles.relativeBadge}>{relBadgeText}</span> : null}
      </span>
    );
  }

  // Normalize value for native date input (YYYY-MM-DD)
  const normalizedIso = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? value
    : /^\d{4}\.\d{2}\.\d{2}$/.test(value)
      ? value.replace(/\./g, "-")
      : "";

  return (
    <Popover open={open} onOpenChange={(_, data) => setOpen(data.open)} positioning="below-start">
      <PopoverTrigger disableButtonEnhancement>
        <button
          type="button"
          className={`${styles.dateTrigger} ${className || ""}`}
          style={style}
          aria-label={ariaLabel || "날짜"}
        >
          <Calendar16Regular style={{ fontSize: "12px", color: tokens.colorNeutralForeground3 }} data-print-hide="" />
          <span>{value || placeholder}</span>
          {relBadgeText ? <span className={styles.relativeBadge}>{relBadgeText}</span> : null}
          <span className={styles.caret} data-print-hide="">
            <ChevronDown12Regular />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverSurface style={{ padding: 0 }}>
        <div className={styles.popoverBody}>
          <div className={styles.chipsRow}>
            {defaultPresets.map((chip) => (
              <button
                key={chip.label}
                type="button"
                className={styles.chip}
                onClick={() => {
                  onChange(chip.iso);
                  setOpen(false);
                }}
              >
                {chip.label}
              </button>
            ))}
          </div>
          <input
            type="date"
            className={styles.dateInput}
            value={normalizedIso}
            onChange={(e) => {
              if (e.target.value) {
                onChange(e.target.value);
                setOpen(false);
              }
            }}
          />
        </div>
      </PopoverSurface>
    </Popover>
  );
}
